// Card selection + the `c`-to-capture keyboard shortcut (issue 3). The dashboard
// renders many capture cards at once, so exactly one card is "selected" at a
// time and the global keydown routes to it. State is deliberately minimal: one
// selected grow id + a registry of each mounted card's capture/discard handlers.
//
// The provider owns ONE window keydown listener (not one per card) and reads the
// current selection + handlers through refs so the listener never goes stale and
// never re-binds. The pure guard logic lives in ./hotkey (unit-tested): `c` only
// fires on a bare keypress outside a text field, so it never hijacks Cmd/Ctrl-C.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

import { isEditableElement, shouldCapture, shouldFireHotkey } from "./hotkey";

export interface CaptureHandlers {
  /** Snapshot the live pen sample into this card's pending reading. */
  capture(): void;
  /** Discard this card's pending reading. */
  discard(): void;
}

interface CaptureSelectionValue {
  selectedGrowId: string | null;
  select(growId: string | null): void;
  // Arm a card on mount ONLY if nothing is selected yet, so a freshly-expanded
  // card doesn't hijack the `c` hotkey from a card the user already armed. An
  // explicit click still calls select(). Reads the live ref so it's stable.
  selectIfUnset(growId: string): void;
  // Cards register a STABLE ref whose .current holds the latest handlers, so the
  // provider never re-registers as the card re-renders. Returns an unregister.
  register(growId: string, ref: MutableRefObject<CaptureHandlers>): () => void;
}

const Ctx = createContext<CaptureSelectionValue | null>(null);

export function CaptureSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedGrowId, setSelectedGrowId] = useState<string | null>(null);
  // Mirror selection into a ref the keydown listener can read without re-binding.
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedGrowId;
  const registry = useRef(new Map<string, MutableRefObject<CaptureHandlers>>());

  const select = useCallback((growId: string | null) => setSelectedGrowId(growId), []);

  const selectIfUnset = useCallback((growId: string) => {
    if (selectedRef.current === null) setSelectedGrowId(growId);
  }, []);

  const register = useCallback(
    (growId: string, ref: MutableRefObject<CaptureHandlers>) => {
      registry.current.set(growId, ref);
      return () => {
        // Only delete if this is still the registered ref (guards a remount race).
        if (registry.current.get(growId) === ref) {
          registry.current.delete(growId);
          // If the card that just unmounted (e.g. collapsed) was the selected
          // one, release the selection so the next expanded card can arm itself.
          if (selectedRef.current === growId) setSelectedGrowId(null);
        }
      };
    },
    [],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const id = selectedRef.current;
      if (!id) return;
      const ref = registry.current.get(id);
      if (!ref) return;
      const editable = isEditableElement(
        e.target as { tagName?: string; isContentEditable?: boolean } | null,
      );
      if (shouldCapture(e, editable)) {
        e.preventDefault();
        ref.current.capture();
        return;
      }
      // Escape discards the pending reading (low-conflict accelerator); save is a
      // deliberate click on the ✓ button, never a stray keypress.
      if (e.key === "Escape" && shouldFireHotkey(e, editable)) {
        ref.current.discard();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo<CaptureSelectionValue>(
    () => ({ selectedGrowId, select, selectIfUnset, register }),
    [selectedGrowId, select, selectIfUnset, register],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The selection context, or null when used outside a provider (capture cards
 *  then work button-only, with no keyboard shortcut). */
export function useCaptureSelection(): CaptureSelectionValue | null {
  return useContext(Ctx);
}
